import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Download } from "lucide-react";
import Page from "@/components/Page";
import { PrimaryLogo } from "@/components/Brand";
import { api, getReturnToken, clearReturnToken } from "@/lib/api";
import { APPLY } from "@/constants/testIds";
import { toast } from "sonner";

export default function Submitted() {
  const { conf } = useParams();
  const navigate = useNavigate();
  const token = getReturnToken();

  const downloadPdf = async () => {
    if (!token) { toast.error("Application token missing"); return; }
    try {
      const res = await api.get(`/applications/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${conf}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not download PDF");
    }
  };

  const withdraw = async () => {
    if (!token) return;
    if (!window.confirm("Withdraw this application? Your uploaded documents will be removed from review. You are always welcome to apply again — there is no penalty for reapplying.")) return;
    try {
      await api.post(`/applications/${token}/withdraw`);
      clearReturnToken();
      toast.success("Application withdrawn.");
      navigate("/");
    } catch (e) {
      toast.error("Could not withdraw.");
    }
  };

  return (
    <Page>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="p2p-card p-8 text-center">
          <div className="flex justify-center mb-4">
            <PrimaryLogo maxWidth={240} />
          </div>
          <CheckCircle2 className="w-12 h-12 mx-auto" style={{ color: "#00A7B5" }} />
          <h1 className="font-serif text-3xl sm:text-4xl mt-3" style={{ color: "#0B2D59" }}>Thank you.</h1>
          <p className="mt-3" style={{ color: "#1F2937" }}>
            Your application has been received. A real person will review it and contact you using your preferred method.
          </p>
          <div className="mt-5 p2p-chip" data-testid={APPLY.confirmation} style={{ fontSize: "1rem", padding: "0.5rem 1rem" }}>
            Confirmation #: <strong className="ml-2">{conf}</strong>
          </div>
          <p className="mt-5 text-sm" style={{ color: "#475569" }}>
            Save your confirmation number. You&apos;ll receive a copy by email if one was provided.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button className="p2p-btn-primary inline-flex items-center gap-2" data-testid={APPLY.downloadPdf} onClick={downloadPdf}>
              <Download className="w-4 h-4" /> Download a copy (PDF)
            </button>
            <button className="p2p-btn-outline" data-testid={APPLY.withdrawBtn} onClick={withdraw}>Withdraw application</button>
            <Link to="/" className="p2p-btn-secondary">Back to home</Link>
          </div>
          <div className="encourage mt-8 text-sm">
            Next steps: we&apos;ll verify your income, contact your references, and reach out to schedule an interview if you qualify
            against the same written criteria we apply to every applicant.
          </div>
        </div>
      </div>
    </Page>
  );
}
